/**
 * /settings/canned-replies — list + create + edit + delete canned
 * replies. Composer in /inbox/:id reads them via the same /canned-replies
 * GET endpoint.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface CannedReplyRow {
  id: string;
  name: string;
  shortcut: string | null;
  body: { text: string };
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute("/_app/settings/canned-replies")({
  component: CannedRepliesPage,
});

const SHORTCUT_RE = /^[a-zA-Z0-9_-]{1,32}$/;

function CannedRepliesPage() {
  const [rows, setRows] = useState<CannedReplyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [shortcut, setShortcut] = useState("");
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ cannedReplies: CannedReplyRow[] }>("/canned-replies");
      setRows(r.data.cannedReplies);
    } catch {
      setError("Failed to load canned replies");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setName("");
    setShortcut("");
    setText("");
    setEditingId(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const trimmedName = name.trim();
    const trimmedShortcut = shortcut.trim();
    const trimmedText = text.trim();
    if (!trimmedName || !trimmedText) return;
    if (trimmedShortcut && !SHORTCUT_RE.test(trimmedShortcut)) {
      setError("Shortcut must be 1-32 chars: letters, numbers, dashes, underscores");
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      name: trimmedName,
      shortcut: trimmedShortcut || null,
      body: { text: trimmedText },
    };
    try {
      if (editingId) {
        await api.patch(`/canned-replies/${editingId}`, payload);
      } else {
        await api.post("/canned-replies", payload);
      }
      resetForm();
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      setError(msg ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(r: CannedReplyRow) {
    setEditingId(r.id);
    setName(r.name);
    setShortcut(r.shortcut ?? "");
    setText(r.body.text);
  }

  async function remove(id: string) {
    if (!confirm("Delete this canned reply?")) return;
    try {
      await api.delete(`/canned-replies/${id}`);
      setRows((cur) => cur.filter((r) => r.id !== id));
      if (editingId === id) resetForm();
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Canned replies</h1>
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          border: "var(--border-hair)",
          borderRadius: 12,
          background: "var(--bg-surface)",
          fontSize: 11,
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 16,
        }}
      >
        Shared across all Instagram accounts
      </span>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Saved snippets the inbox composer drops in with one click. Type
        <code style={{ background: "var(--bg-soft)", padding: "0 4px" }}>/&lt;shortcut&gt;</code>{" "}
        in a reply to filter and pick.
      </p>

      <form
        onSubmit={submit}
        className="brut-card"
        style={{ marginBottom: 24, display: "grid", gap: 10 }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>
          {editingId ? "Edit canned reply" : "New canned reply"}
        </div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 200px" }}>
          <input
            placeholder="Refund policy"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="brut-input"
            maxLength={120}
            required
          />
          <input
            placeholder="refund (optional)"
            value={shortcut}
            onChange={(e) => setShortcut(e.target.value)}
            className="brut-input"
            maxLength={32}
          />
        </div>
        <textarea
          placeholder="Hey! Our refund window is 30 days from purchase…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="brut-input"
          maxLength={2000}
          rows={4}
          required
          style={{ resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="submit"
            disabled={!name.trim() || !text.trim() || busy}
            className="brut-button"
          >
            {busy ? "Saving…" : editingId ? "Save" : "Add"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="brut-button is-secondary"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {error && (
        <div
          className="brut-card"
          style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No canned replies yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Save your most-typed replies above ↑ then drop them into the
            inbox composer with{" "}
            <code style={{ background: "var(--accent)", padding: "0 4px" }}>
              /&lt;shortcut&gt;
            </code>{" "}
            or the picker.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {rows.map((r) => (
            <li
              key={r.id}
              className="brut-card"
              style={{ padding: 14, display: "grid", gap: 6 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{r.name}</div>
                  {r.shortcut && (
                    <code
                      style={{
                        fontSize: 12,
                        color: "var(--muted)",
                      }}
                    >
                      /{r.shortcut}
                    </code>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => startEdit(r)}
                    className="brut-button is-secondary"
                    style={{ fontSize: 13, padding: "6px 10px" }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => void remove(r.id)}
                    className="brut-button is-danger"
                    style={{ fontSize: 13, padding: "6px 10px" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--muted)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {r.body.text}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
